// Share / print the Quota modelling — a branded printable view (browser print →
// Save as PDF, the CCA standard's client-side engine: zero infra, no quota) plus
// CSV/XLSX exports. Covers both the Policy & Run schedule and the 3-way Scenario
// comparison. The print path builds a self-contained document in a new window so
// the output is fully branded and never fights the app's screen layout.
//
// THEME-NEUTRAL (ADR-022): this kernel bakes in no portal signature colour and no
// finance credentials — the consuming app supplies design-system tokens and its
// own footer `attribution` (the finance portal, or the public Quota Model). Shared
// by @cca/xero-web and @cca/quota-model-web; see the package README.

import type { Worksheet } from 'exceljs';
import type {
  QuotaPolicy,
  QuotaRunResponse,
  QuotaScheduleRow,
  ExclusionCategory,
} from '@cca/xero-shared';
import { ccaIconRGB, fmt, triggerDownload, csvBlob, stamp } from '@cca/ui';
import { escapeHtml as esc } from '@cca/safe';

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-NZ')}`;
const pct1 = (x: number): number => Math.round(x * 10) / 10;

const FLAG_LABEL: Record<string, string> = {
  no_data: 'No return data',
  insufficient_years: 'Too few years',
  null_income: 'Missing a year',
  exclusions_exceed_income: 'Exclusions > income',
  carried_forward: 'Carried-forward year',
};

function bandsSummary(p: QuotaPolicy): string {
  return [...p.bands]
    .sort((a, b) => a.from - b.from)
    .map((b) => `${b.from === 0 ? '0' : money(b.from)}+ @ ${pct1(b.rate * 100)}%`)
    .join('; ');
}

function exclusionsSummary(p: QuotaPolicy, categories: ExclusionCategory[]): string {
  const labelOf = new Map(categories.map((c) => [c.category_key, c.label]));
  const on = Object.entries(p.category_exclusion ?? {})
    .filter(([, frac]) => frac > 0)
    .map(([key, frac]) => `${labelOf.get(key) ?? key} ${pct1(frac * 100)}%`);
  return on.length ? on.join(', ') : 'none';
}

/** Plain-English settings as label/value pairs — shared by print, XLSX and CSV. */
export function policyAssumptions(
  p: QuotaPolicy,
  categories: ExclusionCategory[],
): { label: string; value: string }[] {
  const { years, basis_year } = p.smoothing;
  const smoothing =
    years <= 1
      ? basis_year
        ? `single year (${basis_year})`
        : 'single year (latest)'
      : `${years}-year rolling average${basis_year ? ` to ${basis_year}` : ' (latest years)'}`;

  const cap: string[] = [];
  if (p.cap.cap_amount != null) cap.push(money(p.cap.cap_amount));
  if (p.cap.cap_pct_of_assessable != null) cap.push(`${pct1(p.cap.cap_pct_of_assessable * 100)}%`);

  return [
    { label: 'Invoice year', value: String(p.effective_assessment_year ?? '—') },
    { label: 'Income smoothing', value: smoothing },
    { label: 'Exclusions', value: exclusionsSummary(p, categories) },
    {
      label: 'Base exclusion',
      value: p.base_exclusion && p.base_exclusion > 0 ? money(p.base_exclusion) : 'none',
    },
    { label: 'Bands', value: bandsSummary(p) },
    { label: 'Cap', value: cap.length ? cap.join(' or ') : 'none' },
    {
      label: 'Year-on-year cap',
      value:
        p.yoy_increase_cap != null
          ? `+${pct1(p.yoy_increase_cap * 100)}% per year${p.yoy_base_year ? ` from ${p.yoy_base_year}` : ''}`
          : 'none',
    },
    {
      label: 'Phase-in',
      value:
        p.phase_in.enabled && p.phase_in.schedule.length
          ? `factors ${p.phase_in.schedule.join(' / ')} from the invoice year`
          : 'none',
    },
    { label: 'Floor', value: money(p.floor) },
    { label: 'Collar', value: p.collar ? 'on' : 'none' },
  ];
}

const today = (): string =>
  new Date().toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });

// ── Print (branded, browser "Save as PDF") ───────────────────────────────────

const SHEET_CSS = `
  * { box-sizing: border-box; }
  body { font: 13px/1.45 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; color: #111827; margin: 0; padding: 24px; }
  .mast { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #85754d; padding-bottom: 8px; margin-bottom: 4px; }
  .mast .brandwrap { display: flex; align-items: center; gap: 10px; }
  .mast .logo { width: 36px; height: 36px; object-fit: contain; display: block; }
  .mast .brand { font-size: 22px; font-weight: 800; letter-spacing: .04em; color: #005487; }
  .mast .brand small { display: block; font-size: 10px; font-weight: 600; letter-spacing: .12em; color: #6b7280; text-transform: uppercase; }
  .mast .eyebrow { font-size: 11px; color: #6b7280; text-align: right; }
  h1 { font-size: 16px; margin: 12px 0 2px; color: #5c4f33; }
  .sub { color: #6b7280; font-size: 12px; margin-bottom: 14px; }
  .assume { border: 1px solid #e5e0d5; border-radius: 6px; padding: 10px 12px; margin-bottom: 16px; background: #fdfbf6; }
  .assume table { border-collapse: collapse; width: 100%; }
  .assume td { padding: 2px 10px 2px 0; vertical-align: top; font-size: 12px; }
  .assume td.k { color: #6b7280; white-space: nowrap; width: 1%; }
  .assume td.gut { padding-left: 30px; border-left: 1px solid #e5e0d5; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 12px; break-inside: avoid; }
  table.grid thead { display: table-header-group; }
  table.grid tr { break-inside: avoid; }
  table.grid th, table.grid td { padding: 5px 7px; }
  table.grid thead th { border-bottom: 1.5px solid #85754d; color: #5c4f33; text-align: right; }
  table.grid thead th.l, table.grid tbody td.l { text-align: left; }
  table.grid tbody td { text-align: right; border-bottom: 1px solid #eee; }
  table.grid tbody td.l { font-weight: 600; }
  table.grid tfoot td { padding: 7px; font-weight: 700; border-top: 2px solid #85754d; text-align: right; }
  table.grid tfoot td.l { text-align: left; }
  .pos { color: #c62828; } .neg { color: #2e7d32; }
  .flag { font-size: 10px; background: #eee; border-radius: 4px; padding: 1px 5px; margin-left: 4px; white-space: nowrap; }
  .manual { color: #c62828; }
  .foot { margin-top: 18px; color: #6b7280; font-size: 11px; border-top: 1px solid #e5e0d5; padding-top: 8px; }
  .printbtn { margin: 12px 0; }
  .charttitle { font-size: 13px; color: #5c4f33; margin: 18px 0 4px; }
  .clegend { font-size: 11px; color: #6b7280; margin-bottom: 6px; }
  .clegend span { margin-right: 14px; white-space: nowrap; }
  .clegend i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; vertical-align: -1px; margin-right: 4px; }
  .bars { width: 100%; }
  .brow { display: flex; gap: 8px; align-items: center; padding: 2px 0; break-inside: avoid; }
  .bname { width: 175px; flex: 0 0 175px; font-size: 10px; text-align: right; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .btrackwrap { flex: 1; display: flex; flex-direction: column; gap: 2px; }
  .bline { display: flex; align-items: center; gap: 6px; }
  .btrack { flex: 1; background: #efeae0; border-radius: 3px; height: 10px; overflow: hidden; }
  .bfill { height: 100%; border-radius: 3px; }
  .bval { width: 76px; flex: 0 0 76px; font-size: 10px; text-align: right; color: #374151; }
  .pie { display: flex; gap: 18px; align-items: flex-start; break-inside: avoid; margin-bottom: 8px; }
  .plegend { font-size: 11px; }
  .plrow { display: flex; align-items: center; gap: 6px; padding: 1px 0; }
  .plrow i { width: 10px; height: 10px; border-radius: 2px; flex: 0 0 10px; }
  .plname { display: inline-block; max-width: 230px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
  .plval { color: #6b7280; margin-left: 6px; white-space: nowrap; }
  .pierow { display: flex; gap: 28px; flex-wrap: wrap; }
  /* Charts start on a fresh page so the schedule table gets a page to itself. */
  .charts { break-before: page; }
  @media print { .printbtn { display: none; } body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

function openPrint(title: string, bodyHtml: string): void {
  const w = window.open('', '_blank', 'width=1100,height=800');
  if (!w) {
    alert('Please allow pop-ups for this site to print / save as PDF.');
    return;
  }
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
      `<style>${SHEET_CSS}</style>` +
      // Orientation lives in its OWN rule so the Portrait/Landscape buttons can
      // swap it before printing. A4, portrait by default.
      `<style id="cca-page">@page { size: A4 portrait; margin: 12mm; }</style>` +
      `</head><body>` +
      `<div class="printbtn">` +
      `<button id="cca-print-portrait" type="button">Print — Portrait</button> ` +
      `<button id="cca-print-landscape" type="button">Print — Landscape</button>` +
      `</div>` +
      bodyHtml +
      `</body></html>`,
  );
  w.document.close();

  // Print is driven from THIS (opener) context — the popup is about:blank and
  // inherits the host app's CSP (`script-src 'self'`), which blocks any inline
  // script/onclick in the popup. Calling w.print() from the loaded parent bundle
  // is CSP-allowed. Two buttons give the A4 orientation choice; each swaps the
  // @page rule then prints.
  const printWith = (orientation: 'portrait' | 'landscape'): void => {
    const pageRule = w.document.getElementById('cca-page');
    if (pageRule) pageRule.textContent = `@page { size: A4 ${orientation}; margin: 12mm; }`;
    const go = (): void => {
      w.focus();
      w.print();
    };
    // Wait for the masthead logo to load so it lands in the PDF (the same care
    // Corpus takes); fall through immediately if it's already loaded or absent.
    const logo = w.document.getElementById('cca-logo') as HTMLImageElement | null;
    if (logo && !logo.complete) {
      logo.addEventListener('load', go, { once: true });
      logo.addEventListener('error', go, { once: true });
      setTimeout(go, 1500); // fallback if the image stalls
    } else {
      go();
    }
  };
  w.document
    .getElementById('cca-print-portrait')
    ?.addEventListener('click', () => printWith('portrait'));
  w.document
    .getElementById('cca-print-landscape')
    ?.addEventListener('click', () => printWith('landscape'));
}

/** Absolute same-origin URL for the circular CCA icon (shared @cca/ui asset, the
 *  same mark as every PortalHeader). Absolute because the print popup is
 *  about:blank — a root-relative path wouldn't resolve there; img-src 'self'
 *  allows the same-origin absolute URL. */
function logoSrc(): string {
  return new URL(ccaIconRGB, window.location.origin).href;
}

function masthead(eyebrow: string): string {
  return (
    `<div class="mast"><div class="brandwrap">` +
    `<img id="cca-logo" class="logo" src="${esc(logoSrc())}" alt="" />` +
    `<div class="brand">CCAANZ<small>Confessing Anglicans Aotearoa NZ</small></div></div>` +
    `<div class="eyebrow">${esc(eyebrow)}</div></div>`
  );
}

function assumptionsHtml(rows: { label: string; value: string }[]): string {
  // Two columns of label/value pairs (column-major: first half down the left,
  // second half down the right) so the variables take less vertical space and
  // leave more of page 1 for the schedule table.
  type Row = { label: string; value: string };
  const pair = (r: Row | undefined, gutter: boolean): string =>
    `<td class="k${gutter ? ' gut' : ''}">${r ? esc(r.label) : ''}</td>` +
    `<td class="v">${r ? esc(r.value) : ''}</td>`;
  const half = Math.ceil(rows.length / 2);
  const trs: string[] = [];
  for (let i = 0; i < half; i++) {
    trs.push(`<tr>${pair(rows[i], false)}${pair(rows[i + half], true)}</tr>`);
  }
  return `<div class="assume"><table><tbody>${trs.join('')}</tbody></table></div>`;
}

// ── Chart rendering for print (self-contained SVG/HTML, no chart lib) ─────────

const PIE_PALETTE = [
  '#2f6f4f',
  '#8a5a2b',
  '#3a5a8a',
  '#a23b3b',
  '#6b4f9a',
  '#2b8a8a',
  '#9a8a2b',
  '#c2693b',
  '#4f9a5a',
  '#8a3b6b',
];
const SCENARIO_COLORS = ['#2f6f4f', '#8a5a2b', '#3a5a8a'];
const scenarioColor = (label: string): string =>
  SCENARIO_COLORS[['A', 'B', 'C'].indexOf(label)] ?? '#888888';

interface PrintSeries {
  label: string;
  color: string;
}
interface PrintRow {
  name: string;
  values: (number | null)[];
}

/** Horizontal (grouped) bar chart as HTML — bars are divs so they paginate. */
function barChartHtml(
  title: string,
  series: PrintSeries[],
  rows: PrintRow[],
  note?: string,
): string {
  if (rows.length === 0) return '';
  const max = Math.max(
    1,
    ...rows.flatMap((r) => r.values.map((v) => (v == null ? 0 : Math.abs(v)))),
  );
  const legend =
    series.length > 1
      ? `<div class="clegend">${series
          .map((s) => `<span><i style="background:${s.color}"></i>${esc(s.label)}</span>`)
          .join('')}</div>`
      : '';
  const body = rows
    .map((r) => {
      const lines = r.values
        .map((v, i) => {
          const w = v == null ? 0 : (Math.abs(v) / max) * 100;
          const color = series[i]?.color ?? '#999999';
          return (
            `<div class="bline"><div class="btrack">` +
            `<div class="bfill" style="width:${w.toFixed(1)}%;background:${color}"></div></div>` +
            `<div class="bval">${v == null ? '—' : fmt(v)}</div></div>`
          );
        })
        .join('');
      return `<div class="brow"><div class="bname">${esc(r.name)}</div><div class="btrackwrap">${lines}</div></div>`;
    })
    .join('');
  return (
    `<h2 class="charttitle">${esc(title)}</h2>` +
    (note ? `<div class="clegend">${esc(note)}</div>` : '') +
    legend +
    `<div class="bars">${body}</div>`
  );
}

/** Proportion-of-total pie as inline SVG + legend. Top N, rest folded to "Others". */
function pieChartHtml(
  title: string,
  slices: { name: string; value: number }[],
  opts: { topN?: number; size?: number; note?: string } = {},
): string {
  const topN = opts.topN ?? 10;
  const size = opts.size ?? 210;
  const positive = slices.filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
  const total = positive.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return '';
  const top = positive.slice(0, topN);
  const restVal = positive.slice(topN).reduce((s, x) => s + x.value, 0);
  const display = restVal > 0 ? [...top, { name: 'Others', value: restVal }] : top;
  const colorOf = (name: string, i: number): string =>
    name === 'Others' ? '#9a9a9a' : (PIE_PALETTE[i % PIE_PALETTE.length] as string);

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;
  let angle = -Math.PI / 2;
  const paths = display
    .map((s, i) => {
      const frac = s.value / total;
      const a0 = angle;
      const a1 = angle + frac * 2 * Math.PI;
      angle = a1;
      const color = colorOf(s.name, i);
      if (frac >= 0.9999) return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}"/>`;
      const x0 = (cx + r * Math.cos(a0)).toFixed(2);
      const y0 = (cy + r * Math.sin(a0)).toFixed(2);
      const x1 = (cx + r * Math.cos(a1)).toFixed(2);
      const y1 = (cy + r * Math.sin(a1)).toFixed(2);
      const large = frac > 0.5 ? 1 : 0;
      return `<path d="M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z" fill="${color}" stroke="#fff" stroke-width="1"/>`;
    })
    .join('');
  const legend = display
    .map(
      (s, i) =>
        `<div class="plrow"><i style="background:${colorOf(s.name, i)}"></i>` +
        `<span class="plname">${esc(s.name)}</span>` +
        `<span class="plval">${pct1((s.value / total) * 100)}% (${fmt(s.value)})</span></div>`,
    )
    .join('');
  return (
    `<h2 class="charttitle">${esc(title)}</h2>` +
    (opts.note ? `<div class="clegend">${esc(opts.note)}</div>` : '') +
    `<div class="pie"><svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${paths}</svg>` +
    `<div class="plegend">${legend}</div></div>`
  );
}

export function printQuotaRun(opts: {
  run: QuotaRunResponse;
  policy: QuotaPolicy;
  categories: ExclusionCategory[];
  mode: 'draft' | 'stored';
  /** Registrar's baseline run (quota-model app). When present, the second
   *  column mirrors the on-screen table — Baseline instead of assessable. */
  baseline?: { byChurch: Map<string, number | null>; total: number; label?: string | null };
  /** Footer credit — the producing app ("the CCAANZ Finance Portal", or the
   *  public "CCAANZ Quota Model"). Omit for no source line. Parameterised (ADR-022)
   *  so the public tool no longer mis-credits the finance portal. */
  attribution?: string;
}): void {
  const { run, policy, categories, mode, baseline, attribution } = opts;
  const totalCurrent = run.results.reduce((s, r) => s + (r.current_quota ?? 0), 0);
  const totalDelta = run.total_payable - totalCurrent;
  const versionBit =
    mode === 'stored' && run.policy_version ? ` · Policy ${run.policy_version}` : '';

  const rows = run.results
    .map((r) => {
      const delta =
        r.computable && r.current_quota != null ? r.payable_quota - r.current_quota : null;
      const notes = [
        ...(r.computable ? [] : ['<span class="manual">manual assessment</span>']),
        ...r.flags.map((f) => `<span class="flag">${esc(FLAG_LABEL[f] ?? f)}</span>`),
      ].join(' ');
      const baseVal = baseline ? baseline.byChurch.get(r.church_id) : undefined;
      const secondCell = baseline
        ? baseVal == null
          ? '—'
          : fmt(baseVal)
        : r.computable
          ? fmt(r.smoothed_assessable)
          : '—';
      return (
        `<tr><td class="l">${esc(r.name)}</td>` +
        `<td>${secondCell}</td>` +
        `<td>${r.computable ? fmt(r.payable_quota) : '—'}</td>` +
        `<td>${fmt(r.current_quota)}</td>` +
        `<td class="${delta == null ? '' : delta > 0 ? 'pos' : 'neg'}">${delta == null ? '—' : (delta >= 0 ? '+' : '−') + fmt(Math.abs(delta))}</td>` +
        `<td class="l">${notes}</td></tr>`
      );
    })
    .join('');

  const computable = [...run.results]
    .filter((r) => r.computable)
    .sort((a, b) => b.payable_quota - a.payable_quota);
  const charts =
    computable.length > 0
      ? barChartHtml(
          'Engine quota vs current quota, by church',
          [
            { label: 'Engine quota', color: '#2f6f4f' },
            { label: 'Current quota', color: '#b59a6f' },
          ],
          computable.map((r) => ({ name: r.name, values: [r.payable_quota, r.current_quota] })),
          `Computable churches only (${computable.length} of ${run.results.length}), highest first.`,
        ) +
        pieChartHtml(
          'Share of total engine quota, by church',
          computable.map((r) => ({ name: r.name, value: r.payable_quota })),
          { note: 'Top 10; the rest grouped as “Others”.' },
        )
      : '';

  const body =
    masthead(`Diocesan Quota${mode === 'draft' ? ' · DRAFT (not saved)' : ''} · ${today()}`) +
    `<h1>Quota Schedule — Invoice Year ${run.assessment_year}${versionBit}</h1>` +
    `<div class="sub">${run.count} churches · figures in NZD${policy.label ? ` · ${esc(policy.label)}` : ''}${baseline?.label?.trim() ? ` · Baseline: ${esc(baseline.label)}` : ''}</div>` +
    assumptionsHtml(policyAssumptions(policy, categories)) +
    `<table class="grid"><thead><tr>` +
    `<th class="l">Church</th><th>${baseline ? 'Baseline' : 'Smoothed assessable'}</th><th>Engine quota</th><th>Current quota</th><th>Change</th><th class="l">Notes</th>` +
    `</tr></thead><tbody>${rows}</tbody><tfoot><tr>` +
    `<td class="l">Total</td><td>${baseline ? fmt(baseline.total) : ''}</td><td>${fmt(run.total_payable)}</td><td>${fmt(totalCurrent)}</td>` +
    `<td class="${totalDelta > 0 ? 'pos' : 'neg'}">${totalDelta >= 0 ? '+' : '−'}${fmt(Math.abs(totalDelta))}</td><td></td>` +
    `</tr></tfoot></table>` +
    (charts ? `<div class="charts">${charts}</div>` : '') +
    `<div class="foot">Generated ${esc(today())}${attribution ? ` from ${esc(attribution)}` : ''}. Modelling output — engine quota under the Diocesan Quota Model beside each church's current quota.</div>`;

  openPrint(`CCAANZ Quota Schedule ${run.assessment_year}`, body);
}

export interface ScenarioForShare {
  label: string; // A / B / C
  policy: QuotaPolicy;
  year: number;
  result: QuotaRunResponse | null;
}
type RanScenario = ScenarioForShare & { result: QuotaRunResponse };
const hasResult = (s: ScenarioForShare): s is RanScenario => s.result !== null;

/** Union of churches across whichever scenarios have run, in first-seen order. */
function scenarioChurchOrder(
  scenarios: ScenarioForShare[],
): { church_id: string; name: string; current_quota: number | null }[] {
  const order: { church_id: string; name: string; current_quota: number | null }[] = [];
  const seen = new Set<string>();
  for (const s of scenarios) {
    for (const r of s.result?.results ?? []) {
      if (!seen.has(r.church_id)) {
        seen.add(r.church_id);
        order.push({ church_id: r.church_id, name: r.name, current_quota: r.current_quota });
      }
    }
  }
  return order;
}

const payableOf = (s: RanScenario, churchId: string): number | null => {
  const row = s.result.results.find((r) => r.church_id === churchId);
  return row && row.computable ? row.payable_quota : null;
};

export function printQuotaScenarios(opts: {
  scenarios: ScenarioForShare[];
  categories: ExclusionCategory[];
  /** Footer credit — the producing app; omit for no source line (ADR-022). */
  attribution?: string;
}): void {
  const { categories, attribution } = opts;
  const ran = opts.scenarios.filter(hasResult);
  const order = scenarioChurchOrder(opts.scenarios);
  const totalCurrent = order.reduce((s, c) => s + (c.current_quota ?? 0), 0);

  // Assumptions comparison: one column per scenario that has run.
  const perAssume = ran.map((s) => policyAssumptions(s.policy, categories));
  const baseAssume = perAssume[0] ?? [];
  const assumeHead =
    `<tr><td class="k"></td>` +
    ran.map((s) => `<td style="font-weight:700">Scenario ${esc(s.label)}</td>`).join('') +
    `</tr>`;
  const assumeBody = baseAssume
    .map((row, i) => {
      const cells = perAssume.map((a) => `<td>${esc(a[i]?.value ?? '')}</td>`).join('');
      return `<tr><td class="k">${esc(row.label)}</td>${cells}</tr>`;
    })
    .join('');
  const assumeBlock = ran.length
    ? `<div class="assume"><table><tbody>${assumeHead}${assumeBody}</tbody></table></div>`
    : '';

  const bodyRows = order
    .map((c) => {
      const cells = ran
        .map((s) => {
          const p = payableOf(s, c.church_id);
          return `<td>${p == null ? '—' : fmt(p)}</td>`;
        })
        .join('');
      return `<tr><td class="l">${esc(c.name)}</td><td>${fmt(c.current_quota)}</td>${cells}</tr>`;
    })
    .join('');

  const totalCells = ran.map((s) => `<td>${fmt(s.result.total_payable)}</td>`).join('');
  const deltaCells = ran
    .map((s) => {
      const d = s.result.total_payable - totalCurrent;
      return `<td class="${d > 0 ? 'pos' : 'neg'}">${d >= 0 ? '+' : '−'}${fmt(Math.abs(d))}</td>`;
    })
    .join('');
  const headCells = ran.map((s) => `<th>Scenario ${esc(s.label)}</th>`).join('');
  const invoiceYears = Array.from(new Set(ran.map((s) => s.year))).join(', ');

  // Charts: per-church quota across scenarios (grouped bars) + a share pie per scenario.
  const maxP = (cid: string): number => Math.max(0, ...ran.map((s) => payableOf(s, cid) ?? 0));
  const chartChurches = [...order].sort((a, b) => maxP(b.church_id) - maxP(a.church_id));
  const cmpChart = ran.length
    ? barChartHtml(
        'Quota by church, across scenarios',
        [
          { label: 'Current', color: '#b59a6f' },
          ...ran.map((s) => ({ label: `Scenario ${s.label}`, color: scenarioColor(s.label) })),
        ],
        chartChurches.map((c) => ({
          name: c.name,
          values: [c.current_quota, ...ran.map((s) => payableOf(s, c.church_id))],
        })),
        'Current quota beside each scenario, highest first.',
      )
    : '';
  const sharePies = ran.length
    ? `<h2 class="charttitle">Share of total quota, by church</h2><div class="pierow">` +
      ran
        .map((s) =>
          pieChartHtml(
            `Scenario ${s.label}`,
            chartChurches.map((c) => ({ name: c.name, value: payableOf(s, c.church_id) ?? 0 })),
            { size: 190, note: 'Top 10; rest “Others”.' },
          ),
        )
        .join('') +
      `</div>`
    : '';

  const body =
    masthead(`Diocesan Quota · Scenario modelling · ${today()}`) +
    `<h1>Quota Scenario Comparison</h1>` +
    `<div class="sub">${ran.length} scenario${ran.length === 1 ? '' : 's'} · invoice year ${invoiceYears || '—'} · ${order.length} churches · figures in NZD</div>` +
    assumeBlock +
    `<table class="grid"><thead><tr><th class="l">Church</th><th>Current quota</th>${headCells}</tr></thead>` +
    `<tbody>${bodyRows}</tbody>` +
    `<tfoot><tr><td class="l">Total</td><td>${fmt(totalCurrent)}</td>${totalCells}</tr>` +
    `<tr style="font-size:11px"><td class="l">vs current quota</td><td></td>${deltaCells}</tr></tfoot></table>` +
    (cmpChart || sharePies ? `<div class="charts">${cmpChart}${sharePies}</div>` : '') +
    `<div class="foot">Generated ${esc(today())}${attribution ? ` from ${esc(attribution)}` : ''}. Modelling only — nothing here is stored. Use Policy &amp; Run to commit a version.</div>`;

  openPrint('CCAANZ Quota Scenario Comparison', body);
}

// ── CSV / XLSX export ─────────────────────────────────────────────────────────

function runRows(run: QuotaRunResponse): (string | number)[][] {
  const header = [
    'Church',
    'Reg',
    'Smoothed assessable',
    'Engine quota',
    'Share of total % (1dp)',
    'Current quota',
    'Change',
    'Years used',
    'Notes',
  ];
  const body = run.results.map((r: QuotaScheduleRow) => {
    const delta =
      r.computable && r.current_quota != null ? r.payable_quota - r.current_quota : null;
    const share =
      r.computable && run.total_payable > 0
        ? pct1((r.payable_quota / run.total_payable) * 100)
        : '';
    const notes = [
      ...(r.computable ? [] : ['manual assessment']),
      ...r.flags.map((f) => FLAG_LABEL[f] ?? f),
    ].join('; ');
    return [
      r.name,
      r.registration_number ?? '',
      r.computable ? Math.round(r.smoothed_assessable) : '',
      r.computable ? Math.round(r.payable_quota) : '',
      share,
      r.current_quota == null ? '' : Math.round(r.current_quota),
      delta == null ? '' : Math.round(delta),
      r.years_used.join(' '),
      notes,
    ];
  });
  const totalCurrent = run.results.reduce((s, r) => s + (r.current_quota ?? 0), 0);
  const total = [
    'TOTAL',
    '',
    '',
    Math.round(run.total_payable),
    100,
    Math.round(totalCurrent),
    Math.round(run.total_payable - totalCurrent),
    '',
    '',
  ];
  return [header, ...body, total];
}

export function downloadRunCsv(
  run: QuotaRunResponse,
  policy: QuotaPolicy,
  categories: ExclusionCategory[],
): void {
  const meta: (string | number)[][] = [
    ['CCAANZ Diocesan Quota — Schedule'],
    ['Invoice year', run.assessment_year],
    ...(run.policy_version ? [['Policy', run.policy_version]] : []),
    ['Generated', today()],
    ...policyAssumptions(policy, categories).map((a) => [a.label, a.value]),
    [],
  ];
  triggerDownload(
    csvBlob([...meta, ...runRows(run)]),
    `CCAANZ_Quota_Schedule_${run.assessment_year}_${stamp()}.csv`,
  );
}

export async function downloadRunXlsx(
  run: QuotaRunResponse,
  policy: QuotaPolicy,
  categories: ExclusionCategory[],
): Promise<void> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  wb.creator = 'CCAANZ Diocesan Quota Tool';
  wb.created = new Date();

  const ws = wb.addWorksheet('Schedule');
  const rows = runRows(run);
  for (const r of rows) ws.addRow(r);
  styleSheet(ws, rows.length);

  const a = wb.addWorksheet('Assumptions');
  a.addRow(['Quota Schedule', `Invoice year ${run.assessment_year}`]);
  if (run.policy_version) a.addRow(['Policy', run.policy_version]);
  a.addRow(['Generated', today()]);
  a.addRow([]);
  for (const row of policyAssumptions(policy, categories)) a.addRow([row.label, row.value]);
  a.getColumn(1).width = 20;
  a.getColumn(2).width = 60;

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(xlsxBlob(buffer), `CCAANZ_Quota_Schedule_${run.assessment_year}_${stamp()}.xlsx`);
}

function scenarioRows(scenarios: ScenarioForShare[]): (string | number)[][] {
  const ran = scenarios.filter(hasResult);
  const order = scenarioChurchOrder(scenarios);
  const totalCurrent = order.reduce((s, c) => s + (c.current_quota ?? 0), 0);
  const header = [
    'Church',
    'Current quota',
    ...ran.flatMap((s) => [`Scenario ${s.label}`, `Scenario ${s.label} share % (1dp)`]),
  ];
  const body = order.map((c) => [
    c.name,
    c.current_quota == null ? '' : Math.round(c.current_quota),
    ...ran.flatMap((s) => {
      const p = payableOf(s, c.church_id);
      const share =
        p != null && s.result.total_payable > 0 ? pct1((p / s.result.total_payable) * 100) : '';
      return [p == null ? '' : Math.round(p), share];
    }),
  ]);
  const total = [
    'TOTAL',
    Math.round(totalCurrent),
    ...ran.flatMap((s) => [Math.round(s.result.total_payable), 100]),
  ];
  const delta = [
    'vs current quota',
    '',
    ...ran.flatMap((s) => [Math.round(s.result.total_payable - totalCurrent), '']),
  ];
  return [header, ...body, total, delta];
}

export function downloadScenariosCsv(
  scenarios: ScenarioForShare[],
  categories: ExclusionCategory[],
): void {
  const ran = scenarios.filter(hasResult);
  const perAssume = ran.map((s) => policyAssumptions(s.policy, categories));
  const baseAssume = perAssume[0] ?? [];
  const meta: (string | number)[][] = [
    ['CCAANZ Diocesan Quota — Scenario comparison'],
    ['Generated', today()],
    [],
    ['Assumption', ...ran.map((s) => `Scenario ${s.label}`)],
    ...baseAssume.map((row, i) => [row.label, ...perAssume.map((a) => a[i]?.value ?? '')]),
    [],
  ];
  triggerDownload(
    csvBlob([...meta, ...scenarioRows(scenarios)]),
    `CCAANZ_Quota_Scenarios_${stamp()}.csv`,
  );
}

export async function downloadScenariosXlsx(
  scenarios: ScenarioForShare[],
  categories: ExclusionCategory[],
): Promise<void> {
  const ran = scenarios.filter(hasResult);
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  wb.creator = 'CCAANZ Diocesan Quota Tool';
  wb.created = new Date();

  const ws = wb.addWorksheet('Comparison');
  const rows = scenarioRows(scenarios);
  for (const r of rows) ws.addRow(r);
  styleSheet(ws, rows.length);

  const a = wb.addWorksheet('Assumptions');
  a.addRow(['Assumption', ...ran.map((s) => `Scenario ${s.label}`)]);
  a.getRow(1).font = { bold: true };
  const perAssume = ran.map((s) => policyAssumptions(s.policy, categories));
  const baseAssume = perAssume[0] ?? [];
  baseAssume.forEach((row, i) => a.addRow([row.label, ...perAssume.map((p) => p[i]?.value ?? '')]));
  a.getColumn(1).width = 20;
  for (let i = 2; i <= ran.length + 1; i++) a.getColumn(i).width = 34;

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(xlsxBlob(buffer), `CCAANZ_Quota_Scenarios_${stamp()}.xlsx`);
}

// exceljs helpers
function styleSheet(ws: Worksheet, lastRow: number): void {
  const header = ws.getRow(1);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF85754D' } };
  });
  ws.getRow(lastRow).font = { bold: true };
  ws.getColumn(1).width = 34;
}

function xlsxBlob(buffer: ArrayBuffer): Blob {
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
